import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Auth from "@aws-amplify/auth";
import QueryString from "query-string";
import styles from "../styles/Signup.module.scss";
import { useFormFields } from "../libs/hooksLibs";
import classnames from "classnames";
import Log from "../utils/Log";
import { Link } from "react-router-dom";
import { validateText, validateEmail } from "../utils/formUtils";
import isLoggingInFromClient from "../selectors/isLoggingInFromClient";
import ClientSignup from "../components/login/ClientSignup";
import { clientLoginRedirect } from "../actions/current";
import { useDispatch } from "react-redux";
import { getBBVersion } from "../utils/helperUtils";
import { findCookieValue } from "../utils/referralUtils";

//"Container" for sign up logic (ClientSignIn is for UI layout)

export default function Signup(props) {
  const dispatch = useDispatch();
  const {
    isMigration,
    fromBBClient,
    router,
    scrollToLogin,
    setShowConfirmationForm,
    confirmationData,
    windowWidth,
    referral,
    accountlessCheckoutSessionInfo,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [signupErrorText, setSignupErrorText] = useState(null);
  const [productErrorText] = useState(false);
  const [passwordErrorText, setPasswordErrorText] = useState(false);
  const [passwordValidationObject, setPasswordValidation] = useState({
    oneNumber: false,
    eightChars: false,
  });
  const [newUser, setNewUser] = useState(null);
  const [passwordShown, setPasswordShown] = useState(false);
  const isFromWebClient = !useSelector(isLoggingInFromClient);
  const currentUrl = new URL(window.location);
  const urlQuery = QueryString.parse(currentUrl.search);
  const emailIsReadOnly = accountlessCheckoutSessionInfo?.email ? true : false;

  const [fields, updateField] = useFormFields({
    email: accountlessCheckoutSessionInfo?.email || "",
    username: "",
    password: "",
    name: accountlessCheckoutSessionInfo?.name || "",
    confirmPassword: "",
    proposedBuildboxUserId: accountlessCheckoutSessionInfo?.buildboxUserId || "",
    oneTimeCode: accountlessCheckoutSessionInfo?.oneTimeCode || "",
    isGuest: accountlessCheckoutSessionInfo?.isGuest || "",
  });

  const [errorStateObject, updateInputErrorState] = useFormFields({
    email: false,
    username: false,
    name: "",
    password: false,
    confirmPassword: false,
    proposedBuildboxUserId: false,
    oneTimeCode: false,
    isGuest: false,
  });

  useEffect(() => {
    // for some reason this guy likes to come in with a bunch of nulls which can be confusing
    if (accountlessCheckoutSessionInfo && 
      (accountlessCheckoutSessionInfo.email || accountlessCheckoutSessionInfo.buildboxUserId || accountlessCheckoutSessionInfo.oneTimeCode || accountlessCheckoutSessionInfo.isGuest)
    ) {
      updateField("email", accountlessCheckoutSessionInfo?.email || "");
      updateField("proposedBuildboxUserId", accountlessCheckoutSessionInfo?.buildboxUserId || "");
      updateField("oneTimeCode", accountlessCheckoutSessionInfo?.oneTimeCode || "");
      updateField("isGuest", accountlessCheckoutSessionInfo?.isGuest || "");
      sessionStorage.setItem("proposedBuildboxUserId", accountlessCheckoutSessionInfo?.buildboxUserId);
      sessionStorage.setItem("oneTimeCode", accountlessCheckoutSessionInfo?.oneTimeCode);
      sessionStorage.setItem("isGuest", accountlessCheckoutSessionInfo?.isGuest);
      sessionStorage.setItem("afterSignupRedirect", "thankyoupageafterpurchase");
    }
    else {
      updateField("email", "");
      updateField("proposedBuildboxUserId", "");
      updateField("oneTimeCode", "");
      updateField("isGuest", "");
      sessionStorage.removeItem("proposedBuildboxUserId");
      sessionStorage.removeItem("oneTimeCode");
      sessionStorage.removeItem("isGuest");      
    }
    // eslint-disable-next-line
  }, [accountlessCheckoutSessionInfo]);  

  function handleFieldChange(inputName, value) {
    if(inputName === "email" && accountlessCheckoutSessionInfo && accountlessCheckoutSessionInfo.email) {
      // if we have a purchase session email, don't let them change it
      console.log("email is locked to accountless checkout session email");
      return;
    }

    if (validateText(value)) {
      updateInputErrorState(inputName, false);
    } else {
      updateInputErrorState(inputName, true);
    }
    setSignupErrorText(null);

    if (inputName === "username") {
      updateField(inputName, value.replace(/\s+/g, ""));
    } else {
      updateField(inputName, value);
    }

    if (inputName === "password") {
      validatePasswordsOnFieldChange(value);
    }
  }

  const seInputErrorState = (input) => {
    const currentErrorObject = { ...errorStateObject };

    if (input === "password") {
      currentErrorObject["password"] = true;
      currentErrorObject["confirmPassword"] = true;
    } else {
      currentErrorObject[input] = true;
    }
    updateInputErrorState(null, currentErrorObject);
  };

  function validatePasswords() {
    // eslint-disable-next-line
    const pwRegex = RegExp(/^(?=.*\d).{8,}$/);
    setPasswordErrorText(false);

    if (fields.password !== fields.confirmPassword) {
      setSignupErrorText("Passwords do not match");
      // setPasswordErrorText(true);
      seInputErrorState("password");
      return false;
    }

    if (!pwRegex.test(fields.confirmPassword)) {
      setSignupErrorText("Passwords do not match requirements");
      // setPasswordErrorText(true);
      seInputErrorState("password");
      return false;
    }

    return true;
  }

  function validatePasswordsOnFieldChange(value) {
    // const oneNum8CharsRegex = RegExp(/^(?=.*\d).{8,}$/);
    const oneNumber = RegExp(/[\d]{1}/);
    const eightCharacters = value.length >= 8;
    setPasswordErrorText(false); //sets to initial state

    const passwordValidation = { ...passwordValidationObject };

    passwordValidation["oneNumber"] = oneNumber.test(value);
    passwordValidation["eightChars"] = eightCharacters;

    setPasswordValidation(passwordValidation);

    // if (!oneNum8CharsRegex.test(value)) {
    //   setPasswordErrorText(true); //sets to red
    // }
  }

  function validateEmailField() {
    if (!validateEmail(fields.email)) {
      setSignupErrorText("Invalid email format");
      updateInputErrorState("email", true);

      return false;
    }

    return true;
  }

  function validateForm() {
    // Fields to exclude from validation (the hidden ones)
    const excludedFields = ["proposedBuildboxUserId", "oneTimeCode", "isGuest"];
    
    // Filter out the hidden fields from validation
    const filteredFields = Object.keys(fields).filter(key => !excludedFields.includes(key));
    
    const values = Object.values(filteredFields);
    let fieldsAreValid = values.every((value) => value !== "");

    if (!fieldsAreValid) {
      const currentErrorObject = { ...errorStateObject };
      for (const inputName in fields) {
        if (!validateText(fields[inputName])) {
          currentErrorObject[inputName] = true;
        }
      }
      setSignupErrorText("Please fill out all required fields");
      updateInputErrorState(null, currentErrorObject);
    }

    return fieldsAreValid;
  }

  function getErrorMessage(error) {
    let signupErrorMessage = error.message;

    switch (error.code) {
      case "InvalidParameterException":
        signupErrorMessage = "Username cannot be of email format";
        seInputErrorState("username");
        break;
      case "UsernameExistsException":
        signupErrorMessage =
          "The username already exists. \n Please try a different one.";
        seInputErrorState("username");
        break;
      case "UserLambdaValidationException":
        signupErrorMessage =
          "Oops, looks like you're already registered, \n please log in to your existing account";
        break;
      default:
        break;
    }

    return signupErrorMessage;
  }

  async function handleSignup() {
    let productType = sessionStorage.getItem("productType");
    sessionStorage.setItem("signupEmail", fields.email);

    if (isFromWebClient) {
      if (!productType) {
        // TODO: is there a better default here? 'generic' maybe? not sure what all effects it has...
        productType = "generic";
      }
    } 
    else {
      if (urlQuery["view"] && urlQuery["view"].toLowerCase() === "bb2") {
        productType = "BB2";
      } 
      else if (urlQuery["view"] && urlQuery["view"].toLowerCase() === "bb3") {
        productType = "BB3";
      }
      else if (urlQuery["view"] && urlQuery["view"].toLowerCase() === "bb4") {
        productType = "BB4";
      }      
      else if(urlQuery["view"] && urlQuery["view"].toLowerCase() === "soundbox") {
        productType = "Soundbox";
      }
      else if(urlQuery["view"] && urlQuery["view"].toLowerCase() === "bbbundle") {
        productType = "BB4";
      }      
      else {
        console.error("unrecognized product view")
        productType = "unknown";
      }
    }

    Log.info(productType, "User is signing up with product type:");

    let xsollaTrackingId = findCookieValue("tracking_id=");

    xsollaTrackingId = !xsollaTrackingId ? "" : xsollaTrackingId;

    // for a moment I thought about adding more custom fields here
    // but then I remembered what an enormous chore it is on Cognito
    // defining the fields, granting permission, usually requiring changes to the "client app"
    // not worth it just to write "checkout session id" in a new place or whatever.
    const user = await Auth.signUp({
      username: fields.username,
      password: fields.password,
      attributes: {
        email: fields.email,
        name: fields.name,
        "custom:isMigration": isMigration,
        "custom:productType": getBBVersion(productType),
        "custom:referralTrackingId": xsollaTrackingId,
      },
    });

    Log.info(user, "new user!");
    if (user.userConfirmed) {
      if (fromBBClient) {
        await dispatch(clientLoginRedirect(fields.username, fields.password));
      } 
      else {
        await Auth.signIn(fields.username, fields.password);
      }
    } 
    else {
      setNewUser(user);
      if (setShowConfirmationForm) {
        const confirmationObject = {
          username: fields.username,
          password: fields.password,
        };
        setShowConfirmationForm(confirmationObject);
      }
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm() || !validatePasswords() || !validateEmailField()) {
      return false;
    }

    setIsLoading(true);

    try {
      await handleSignup();
    } catch (e) {
      Log.error(e.message, "Signup.js handleSubmit error:");

      const errorMessage = getErrorMessage(e);
      setSignupErrorText(errorMessage);

      /**
       * Handling refresh while signing up
       * Check for the UsernameExistsException in the handleSubmit function’s catch block.
       * Use the Auth.resendSignUp() method to resend the code if the user has not been previously confirmed. Here is a link to the Amplify API docs.
       * Confirm the code just as we did before. */
      setIsLoading(false);
    }
    return true;
  }

  function togglePasswordVisiblity() {
    setPasswordShown(!passwordShown);
  }

  const inputStyles = {
    container: styles.signupContainer,
    label: styles.signupInputLabel,
    input: styles.signupInput,
    error: styles.signupInputError,
    icon: classnames(styles.passwordIcon, styles.web),
  };

  function showErrorText() {
    if (signupErrorText) {
      return signupErrorText;
    } else if (productErrorText) {
      return (
        <Link
          className={classnames(
            styles.passwordRequirementDefault,
            styles.error
          )}
          to={"/plans"}
        >
          You must select a product in order to sign up. Please click here and
          choose a plan and try again.
        </Link>
      );
    } else {
      return null;
    }
  }

  const isGuestCheckoutRegistration = accountlessCheckoutSessionInfo?.isGuest === "1";

  const signupProps = {
    fields,
    errorStateObject,
    handleFieldChange,
    isLoading,
    signupErrorText,
    newUser,
    passwordShown,
    fromBBClient,
    router,
    handleSubmit,
    togglePasswordVisiblity,
    inputStyles,
    passwordErrorText,
    showErrorText,
    scrollToLogin,
    confirmationData,
    passwordValidationObject,
    windowWidth,
    referral,
    isGuestCheckoutRegistration,
    emailIsReadOnly,
  };

  return (
    <div className={styles.signup}>
      <ClientSignup {...signupProps} />
    </div>
  );
}
